import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModalModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { LoaderService } from 'src/app/services/loader.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { CommonService } from 'src/app/services/common.service';

@Component({
  selector: 'projects-certificate',
  imports: [
    ReactiveFormsModule,
    NgbModalModule,
    LoaderComponent
],
  templateUrl: './projects-certificate.component.html',
  styleUrl: './projects-certificate.component.scss'
})
export class ProjectsCertificateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId?: string;
  @Output() onCertificateUpdated = new EventEmitter<any>();
  
  certificates: any[] = [];
  activeCertificateRows: CertificateDisplayRow[] = [];
  inactiveCertificateRows: CertificateDisplayRow[] = [];
  readonly LOADER_CONTEXT = 'projects-certificate';
  isLoading: boolean = false;
  private subscription = new Subscription();

  uploadForm: FormGroup;
  commentForm: FormGroup;
  selectedCertificate: any;
  selectedPrimaryFile: File | null = null;
  selectedHashCodeFile: File | null = null;

  certificateToDelete: any = null;

  canManageActive: boolean = false;
  canUploadCertificates: boolean = false;
  canViewHashCode: boolean = false;
  canManageCertificateRow: boolean = false;
  showInactiveTable: boolean = false;
  private pendingActiveChanges = new Map<number, boolean>();
  private originalActiveById = new Map<number, boolean>();

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private alertNotificationService: AlertNotificationService,
    private projectsService: ProjectsService,
    private loaderService: LoaderService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private commonService: CommonService
  ) {
    this.commentForm = this.fb.group({
      comment: ['', Validators.required]
    });

    this.uploadForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this.subscription.add(
      this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT).subscribe(loading => {
        this.isLoading = loading;
      })
    );

    this.subscription.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        this.canManageActive = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_CERTIFICATE_ACTIVE_TOGGLE', 'modify'
        );
        this.canUploadCertificates = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_CERTIFICATE_UPLOAD', 'modify'
        );
        this.canViewHashCode = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_CERTIFICATE_HASH_CODE', 'view'
        );
        this.canManageCertificateRow = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_CERTIFICATE_MANAGE_ROW', 'modify'
        );
        this.showInactiveTable = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_CERTIFICATE_INACTIVE_TABLE', 'view'
        );
        this.rebuildCertificateRows();
      })
    );
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.loadCertificates();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.loadCertificates();
    }
  }
  
  onFileSelected(event: Event, target: 'primary' | 'secondary'): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length) {
      const file = input.files[0];
      if (target === 'primary') {
        this.selectedPrimaryFile = file;
      } else {
        this.selectedHashCodeFile = file;
      }
    }
  }
  
  uploadCertificates(): void {
    if (!this.canUploadCertificates) return;

    const projectId = this.projectId;
    if (!projectId) return;

    if (!this.selectedPrimaryFile && !this.selectedHashCodeFile) return;

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    const formData = new FormData();
    const data = { description: '' };

    formData.append('data', JSON.stringify(data));
    if (this.selectedPrimaryFile) {
      formData.append('fileCertificate', this.selectedPrimaryFile, this.selectedPrimaryFile.name);
    }
    if (this.selectedHashCodeFile) {
      formData.append('fileHash', this.selectedHashCodeFile, this.selectedHashCodeFile.name);
    }

    this.projectsService.createProjectCertificate(+projectId, formData).subscribe({
      next: (response) => {
        this.alertNotificationService.success('Reports uploaded successfully');
        this.uploadForm.reset();
        this.selectedPrimaryFile = null;
        this.selectedHashCodeFile = null;

        const primaryInput = document.getElementById('primaryCertificateFile') as HTMLInputElement;
        if (primaryInput) {
          primaryInput.value = '';
        }

        const secondaryInput = document.getElementById('secondaryCertificateFile') as HTMLInputElement;
        if (secondaryInput) {
          secondaryInput.value = '';
        }

        this.loadCertificates();

        this.onCertificateUpdated.emit({
          type: 'certificate',
          action: 'upload',
          certificateIds: [response.data?.projectCertificateId],
          status: 'uploaded'
        });
      },
      error: (error) => {
        console.error('Error uploading certificates:', error);
        this.alertNotificationService.error('Error uploading reports');
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  loadCertificates(): void {
    if (!this.projectId) return;
    
    this.loaderService.startLoading(this.LOADER_CONTEXT);
    
    this.projectsService.listProjectCertificate(+this.projectId).subscribe({
      next: (response) => {
        this.certificates = response.data?.projectCertificates || [];
        this.pendingActiveChanges.clear();
        this.originalActiveById.clear();
        this.certificates.forEach(certificate => {
          this.originalActiveById.set(certificate.projectCertificateId, certificate.active !== false);
        });
        this.rebuildCertificateRows();

        this.onCertificateUpdated.emit({
          type: 'certificate',
          count: this.certificates.length,
          status: 'loaded'
        });
      },
      error: (error) => {
        console.error('Error loading certificates:', error);
        this.alertNotificationService.error('Error loading reports');
        this.certificates = [];
        this.pendingActiveChanges.clear();
        this.originalActiveById.clear();
        this.rebuildCertificateRows();
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  private rebuildCertificateRows(): void {
    const activeCertificates = this.certificates.filter(certificate => certificate.active !== false);
    const inactiveCertificates = this.certificates.filter(certificate => certificate.active === false);
    this.activeCertificateRows = this.buildDisplayRows(activeCertificates);
    this.inactiveCertificateRows = this.buildDisplayRows(inactiveCertificates);
  }

  onToggleActive(row: CertificateDisplayRow): void {
    if (!this.canManageActive) return;

    const certificate = row.source;
    const newValue = certificate.active === false;
    certificate.active = newValue;

    const certificateId = certificate.projectCertificateId;
    const originalValue = this.originalActiveById.get(certificateId) ?? true;

    if (newValue === originalValue) {
      this.pendingActiveChanges.delete(certificateId);
    } else {
      this.pendingActiveChanges.set(certificateId, newValue);
    }

    this.rebuildCertificateRows();
  }

  submitForm(): void {
    if (this.pendingActiveChanges.size === 0 || !this.projectId) {
      return;
    }

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    const projectId = +this.projectId;
    const changes = Array.from(this.pendingActiveChanges.entries());

    const requests = changes.map(([certificateId, active]) =>
      this.projectsService.updateProjectCertificate(projectId, certificateId, { active }).pipe(
        catchError(error => {
          console.error(`Error updating certificate ${certificateId}:`, error);
          return of({ error: true, certificateId });
        })
      )
    );

    forkJoin(requests).subscribe({
      next: (results: any[]) => {
        const failed = results.filter(result => result?.error);
        const succeededCount = results.length - failed.length;

        if (succeededCount > 0) {
          this.alertNotificationService.success(`${succeededCount} report(s) updated successfully`);
        }

        if (failed.length > 0) {
          this.alertNotificationService.error(`Error updating ${failed.length} report(s)`);
        }

        this.onCertificateUpdated.emit({
          type: 'certificate',
          action: 'toggleActive',
          status: 'updated'
        });

        this.loadCertificates();
      },
      error: (error) => {
        console.error('Error updating certificates:', error);
        this.alertNotificationService.error('Error updating reports');
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  formatCreateTime(createTime: string): string {
    if (!createTime) return '';
    const date = new Date(createTime);
    if (isNaN(date.getTime())) return '';
    return this.commonService.formatDate(date);
  }

  downloadAttachment(attachment: any): void {
    if (!attachment?.url) return;
    if (attachment.category === 'HASH' && !this.canViewHashCode) return;

    this.loaderService.startLoading(this.LOADER_CONTEXT);
    this.projectsService.GET(attachment.url).subscribe({
      next: (response: any) => {
        window.open(response.data.attachment.publicUrl, '_blank');
      },
      error: (error: any) => {
        this.alertNotificationService.error('Error downloading file');
        console.error('Error:', error);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  openCommentModal(certificate: any, modal: any): void {
    if (!this.canManageCertificateRow) return;

    this.selectedCertificate = certificate;
    this.commentForm.patchValue({
      comment: certificate.description || ''
    });
    this.modalService.open(modal, { centered: true });
  }

  saveComment(): void {
    if (this.commentForm.valid && this.selectedCertificate && this.projectId) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);
      
      const data = {
        description: this.commentForm.get('comment')?.value
      };
      
      this.projectsService.updateProjectCertificate(
        +this.projectId, 
        this.selectedCertificate.projectCertificateId, 
        data
      ).subscribe({
        next: (response) => {
          this.alertNotificationService.success('Comment saved successfully');
          this.modalService.dismissAll();
          this.loadCertificates();
          
          this.onCertificateUpdated.emit({
            type: 'certificate',
            action: 'comment',
            certificateId: this.selectedCertificate.projectCertificateId,
            status: 'updated'
          });
        },
        error: (error) => {
          console.error('Error saving comment:', error);
          this.alertNotificationService.error('Error saving comment');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        },
        complete: () => {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }

  deleteCertificate(certificate: any): void {
    if (!this.canManageCertificateRow) return;

    this.certificateToDelete = certificate;
    const certificateName = certificate.attachments?.[0]?.fileNameOriginal || 'this report';
    this.confirmationDialogService.open(
      `Are you sure you want to delete the report <strong>${certificateName}</strong>?`,
      () => this.onConfirmDelete()
    );
  }
  
  onConfirmDelete(): void {
    if (this.certificateToDelete && this.projectId) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);
      
      this.projectsService.deleteProjectCertificate(
        +this.projectId, 
        this.certificateToDelete.projectCertificateId
      ).subscribe({
        next: (response) => {
          this.alertNotificationService.success('Report deleted successfully');
          this.loadCertificates();
          
          this.onCertificateUpdated.emit({
            type: 'certificate',
            action: 'delete',
            certificateId: this.certificateToDelete.projectCertificateId,
            status: 'deleted'
          });
          
          this.closeConfirmationDialog();
        },
        error: (error) => {
          console.error('Error deleting certificate:', error);
          this.alertNotificationService.error('Error deleting report');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        },
        complete: () => {
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }
  
  closeConfirmationDialog(): void {
    this.certificateToDelete = null;
  }

  private buildDisplayRows(certificates: any[]): CertificateDisplayRow[] {
    const rows: CertificateDisplayRow[] = [];

    certificates.forEach((certificate) => {
      const attachments = Array.isArray(certificate.attachments)
        ? certificate.attachments
        : [];
      const certificateAttachments = attachments.filter(
        (attachment: any) => attachment.category === 'CERTIFICATE'
      );
      const hashAttachments = this.canViewHashCode
        ? attachments.filter((attachment: any) => attachment.category === 'HASH')
        : [];

      if (certificateAttachments.length === 0) {
        const fallbackCandidates = this.canViewHashCode
          ? attachments
          : attachments.filter((attachment: any) => attachment.category !== 'HASH');
        const fallbackAttachment = fallbackCandidates.length > 0 ? fallbackCandidates[0] : null;

        if (!fallbackAttachment && attachments.length > 0) {
          return;
        }

        rows.push({
          source: certificate,
          certificateAttachment: fallbackAttachment,
          hashAttachments: []
        });
        return;
      }

      const hashMap = this.mapHashesToCertificates(
        certificateAttachments,
        hashAttachments
      );

      certificateAttachments.forEach((certificateAttachment: any, index: number) => {
        rows.push({
          source: certificate,
          certificateAttachment,
          hashAttachments: hashMap[index] || []
        });
      });
    });

    return rows;
  }

  private mapHashesToCertificates(certs: any[], hashes: any[]): any[][] {
    const map = certs.map(() => [] as any[]);

    if (hashes.length === 0) {
      return map;
    }

    if (hashes.length === certs.length) {
      hashes.forEach((hash, index) => {
        map[index] = [hash];
      });
      return map;
    }

    if (certs.length === 1) {
      map[0] = hashes;
      return map;
    }

    if (hashes.length === 1) {
      map[0] = hashes;
      return map;
    }

    map[0] = hashes;
    return map;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}

interface CertificateDisplayRow {
  source: any;
  certificateAttachment: any;
  hashAttachments: any[];
}
